import { useContext, useEffect } from 'react';
import { useAlert } from 'react-alert';
import { Button as BootstrapButton, Form } from 'react-bootstrap';
import CopyToClipboard from 'react-copy-to-clipboard';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import I18n from 'i18n-js';
import PropTypes from 'prop-types';

import {
  cardsItemsSelector,
  fetchCardIfNeeded,
  getReward,
  shredCard,
} from '@lootboy/lib/slices/cards';

import Alert from '~/components/Alert';
import ShareAndroidIcon from '~/components/atoms/Icons/ShareAndroidIcon';
import TrashIcon from '~/components/atoms/Icons/TrashIcon';
import XIcon from '~/components/atoms/Icons/XIcon';
import { Action } from '~/components/BootstrapDialog';
import Button from '~/components/Button';
import CardsShredPopup from '~/components/CardsShredPopup';
import CardTemplateView from '~/components/CardTemplateView';
import ContentView from '~/components/ContentView';
import { CookieConsentsContext } from '~/components/CookieConsentsProvider';
import GoogleTagManager, {
  recordOpenScreenEvent,
} from '~/components/GoogleTagManager';
import LootGemQuestView from '~/components/LootGemQuestView';
import MultistageQuestView from '~/components/MultistageQuestView';
import NavigationBar from '~/components/NavigationBar';
import PopupContent from '~/components/PopupContent';

import { ECookieProcessingServices } from '~/constants/cmp';
import useBooleanState from '~/hooks/useBooleanState';
import { useDocumentTitle } from '~/hooks/useDocumentTitle';
import useOnClose from '~/hooks/useOnClose';
import { pushDialog } from '~/redux/actions/AlertActions';
import { countrySelector } from '~/redux/selectors';
import { CARDS_PATH } from '~/routes/paths';

import ActionButton, { ActionButtonItem } from './ActionButton';
import RewardCodeView from './RewardCodeView';

import style from './CardDetailView.module.scss';

function CardDetailView({ className, history, match }) {
  const alert = useAlert();
  const dispatch = useDispatch();

  const country = useSelector(countrySelector);
  const cards = useSelector(cardsItemsSelector);

  const onClose = useOnClose();
  const [
    showShredPopup,
    { setTrue: setShowShredPopupTrue, setFalse: setShowShredPopupFalse },
  ] = useBooleanState();

  const {
    acceptService,
    consents: { [ECookieProcessingServices.SOVENDUS]: hasSovendusConsent },
  } = useContext(CookieConsentsContext);

  const { cardId } = match.params;
  const card = cards[cardId];

  const questProgress = card?.questProgress;

  useEffect(() => {
    dispatch(fetchCardIfNeeded(cardId)).catch((err) => {
      alert.error(
        <Alert
          body={err.message}
          title={I18n.t('common_error')}
        />,
      );
      history.replace(CARDS_PATH);
    });

    GoogleTagManager.dataLayer({
      event: 'PageView',
      pageName: '/card',
    });

    recordOpenScreenEvent('carddetail', {
      card_template_id: card?.cardTemplate?._id,
    });
  }, [card, cardId, dispatch, history, alert]);

  useDocumentTitle(card?.cardTemplate?.name);

  const share = () => {
    const shareText = I18n.t('CardDetailView_share_card_message', {
      cardTemplateName: card?.cardTemplate?.name,
    });
    const host =
      window.location.href &&
      window.location.href.indexOf('www.lootboy.com') !== -1
        ? 'www.lootboy.com'
        : 'www.lootboy.de';
    const url = `https://${host}`;

    dispatch(
      pushDialog({
        title: I18n.t('CardDetailView_action_share_card_web'),
        body: (
          <div>
            <Form.Control
              as="textarea"
              readOnly
              style={{ minHeight: '100px' }}
              value={`${shareText}\n${url}`}
            />
          </div>
        ),
        actions: [
          Action(
            undefined,
            undefined,
            undefined,
            <CopyToClipboard
              onCopy={() =>
                alert.info(
                  <Alert
                    body={I18n.t('common_copied_to_clipboard_msg')}
                    title={I18n.t('common_copied_to_clipboard_title')}
                  />,
                )
              }
              text={`${shareText}\n${url}`}
            >
              <BootstrapButton variant="primary">
                {I18n.t('common_copy_to_clipboard')}
              </BootstrapButton>
            </CopyToClipboard>,
          ),
        ],
      }),
    );
  };

  const shred = async () => {
    return dispatch(shredCard(cardId)).then((response) => {
      if (response && response.gotBonus) {
        history.goBack();
      }
    });
  };

  const getRewardAction = () => {
    dispatch(getReward(cardId))
      .then((res) => {
        if (res?.uriType === 'sovendus' && !hasSovendusConsent) {
          return acceptService(ECookieProcessingServices.SOVENDUS);
        }
        return null;
      })
      .catch((err) => {
        alert.error(
          <Alert
            body={err.message}
            title={I18n.t('common_error')}
          />,
        );
      });
  };

  const renderCardImage = () => {
    if (!card) {
      return null;
    }

    return (
      <div className={style.cardImageContainer}>
        <CardTemplateView
          cardTemplate={card.cardTemplate}
          className={style.cardTemplate}
        />
      </div>
    );
  };

  const renderCode = () => {
    if (!card) {
      return null;
    }

    return (
      <RewardCodeView
        card={card}
        country={country}
        onOpened={getRewardAction}
      />
    );
  };

  const renderContentBlocks = (contentArray) => {
    if (!Array.isArray(contentArray)) {
      return null;
    }

    return contentArray.map((content, index) => (
      <ContentView
        className={style.content}
        key={index}
        {...content}
      />
    ));
  };

  const renderQuestProgress = () => {
    let questProgressProps = null;
    if (
      card &&
      card.questProgress &&
      card.questProgress.quests &&
      card.questProgress.quests.length > 0
    ) {
      const latestQuestStage = card.questProgress.quests.slice(-1)[0];
      questProgressProps = {
        quest: latestQuestStage.quest,
        isUsernameRequired:
          latestQuestStage.quest.isUsernameRequired ||
          card.questProgress.isUsernameRequired,
        availableFrom: latestQuestStage.availableFrom,
        expiresAt: latestQuestStage.expiresAt,
        questProgress: card.questProgress,
        cardId: card._id,
      };
    }

    return (
      card &&
      card.questProgress &&
      (card.questProgress.quests && card.questProgress.quests.length > 0 ? (
        <MultistageQuestView {...questProgressProps} />
      ) : (
        <LootGemQuestView
          {...card?.questProgress}
          cardId={card?._id}
          isUsernameRequired={
            card?.cardTemplate?.quest &&
            card?.cardTemplate?.quest?.isUsernameRequired
          }
        />
      ))
    );
  };

  return (
    <PopupContent
      className={classNames(style.cardDetail, style[className])}
      loading={!card || !card.lastSuccessfulFetch}
      onClose={onClose}
    >
      <div className={style.headerContainer}>
        <NavigationBar
          buttonsClassname={style.backButton}
          className={style.navigationBar}
          Button={XIcon}
          onClose={onClose}
        >
          <ShareAndroidIcon
            className={style.shareButton}
            onClick={share}
          />
          <TrashIcon
            className={style.shredButton}
            onClick={setShowShredPopupTrue}
          />
          <ActionButton>
            <ActionButtonItem
              onClick={share}
              title={I18n.t('CardDetailView_action_share_card')}
            >
              <ShareAndroidIcon />
            </ActionButtonItem>
            <ActionButtonItem
              onClick={setShowShredPopupTrue}
              title={I18n.t('CardDetailView_action_shred_card')}
            >
              <TrashIcon />
            </ActionButtonItem>
          </ActionButton>
        </NavigationBar>
        {renderCardImage()}
      </div>
      <div className={style.cardContent}>
        <h1>{card?.cardTemplate?.name}</h1>
        <div>{renderContentBlocks(card?.cardTemplate?.content)}</div>
        <div className={style.afterPurchaseContent}>
          {renderContentBlocks(card?.cardTemplate?.afterPurchaseContent)}
        </div>

        {questProgress && questProgress.status === 'COMPLETED' ? (
          <div className={style.cardCodeContainer}>
            <h3>{I18n.t('LootGemQuestView_quests_completed_message')}</h3>
            <Button
              className={style.downloadButton}
              onClick={shred}
              size="X4"
            >
              {I18n.t('LootGemQuestView_quests_completed_shred_button_label')}
            </Button>
          </div>
        ) : (
          <div>
            {!card?.cardTemplate?.hideReward && (
              <div className={style.cardCodeContainer}>{renderCode()}</div>
            )}

            {renderQuestProgress()}
          </div>
        )}
      </div>
      <CardsShredPopup
        cards={[card]}
        show={showShredPopup}
        closePopup={setShowShredPopupFalse}
        shred={shred}
      />
    </PopupContent>
  );
}

CardDetailView.propTypes = {
  history: PropTypes.object.isRequired,
  country: PropTypes.string,
  className: PropTypes.string,
  match: PropTypes.object.isRequired,
};

export default CardDetailView;
